/**
 * Date Preset Configuration
 *
 * Standard scheduling presets for quick date selection.
 * All presets use startOf('day') (midnight) since that's our default time.
 * Presets focus on START of periods to align with midnight convention.
 *
 * When timezone is provided, calculations are done in that timezone,
 * ensuring "midnight" means midnight in the organization's timezone.
 */

import dayjs, { type Dayjs } from 'dayjs'
import type { DatePreset } from '../types'

/**
 * Get "now" in the specified timezone (or local if not provided).
 * This is the base for all date calculations.
 */
function getNow(timezone?: string): Dayjs {
  if (timezone) {
    return dayjs().tz(timezone)
  }
  return dayjs()
}

/**
 * Get today's date at midnight.
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 */
export function getToday(timezone?: string): Dayjs {
  return getNow(timezone).startOf('day')
}

/**
 * Get tomorrow's date at midnight.
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 */
export function getTomorrow(timezone?: string): Dayjs {
  return getNow(timezone).add(1, 'day').startOf('day')
}

/**
 * Get the start of next week (Sunday).
 * US weeks start on Sunday, so this returns the next Sunday.
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 */
export function getNextWeek(timezone?: string): Dayjs {
  const today = getNow(timezone)
  const currentDay = today.day() // 0 = Sunday, 1 = Monday, etc.
  // Days until next Sunday (7 - currentDay, or 7 if today is Sunday)
  const daysUntilSunday = currentDay === 0 ? 7 : 7 - currentDay
  return today.add(daysUntilSunday, 'day').startOf('day')
}

/**
 * Get the first day of next month.
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 */
export function getNextMonth(timezone?: string): Dayjs {
  return getNow(timezone).add(1, 'month').startOf('month')
}

/**
 * Get the first day of next quarter.
 * Q1 = Jan 1, Q2 = Apr 1, Q3 = Jul 1, Q4 = Oct 1
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 */
export function getNextQuarter(timezone?: string): Dayjs {
  const now = getNow(timezone)
  const month = now.month() // 0-11
  // Current quarter: 0-2 = Q1, 3-5 = Q2, 6-8 = Q3, 9-11 = Q4
  // Next quarter start month: Q1->Apr(3), Q2->Jul(6), Q3->Oct(9), Q4->Jan(0) next year
  const currentQuarter = Math.floor(month / 3) // 0, 1, 2, or 3
  const nextQuarterStartMonth = ((currentQuarter + 1) % 4) * 3 // 3, 6, 9, or 0

  if (nextQuarterStartMonth === 0) {
    // Next quarter is Q1 of next year
    return now.add(1, 'year').month(0).startOf('month')
  }
  return now.month(nextQuarterStartMonth).startOf('month')
}

/**
 * Get the first day of next year (Jan 1).
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 */
export function getNextYear(timezone?: string): Dayjs {
  return getNow(timezone).add(1, 'year').startOf('year')
}

/**
 * Get date 30 days from now.
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 */
export function getPlus30Days(timezone?: string): Dayjs {
  return getNow(timezone).add(30, 'day').startOf('day')
}

/**
 * Get date 90 days from now.
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 */
export function getPlus90Days(timezone?: string): Dayjs {
  return getNow(timezone).add(90, 'day').startOf('day')
}

/**
 * Default presets shown in the picker (browser timezone).
 * Focused on START of periods to align with midnight default time.
 * For timezone-aware presets, use createDefaultPresets(timezone).
 */
export const DEFAULT_PRESETS: DatePreset[] = [
  { key: 'tomorrow', label: 'Tomorrow', getValue: () => getTomorrow() },
  { key: 'next_week', label: 'Next Week', getValue: () => getNextWeek() },
  { key: 'next_month', label: 'Next Month', getValue: () => getNextMonth() },
  { key: 'next_quarter', label: 'Next Qtr', getValue: () => getNextQuarter() },
  { key: 'next_year', label: 'Next Year', getValue: () => getNextYear() },
]

/**
 * Extended presets including relative day offsets (browser timezone).
 * For timezone-aware presets, use createExtendedPresets(timezone).
 */
export const EXTENDED_PRESETS: DatePreset[] = [
  { key: 'today', label: 'Today', getValue: () => getToday() },
  { key: 'tomorrow', label: 'Tomorrow', getValue: () => getTomorrow() },
  { key: 'next_week', label: 'Next Week', getValue: () => getNextWeek() },
  { key: 'next_month', label: 'Next Month', getValue: () => getNextMonth() },
  { key: 'plus_30', label: '+30d', getValue: () => getPlus30Days() },
  { key: 'plus_90', label: '+90d', getValue: () => getPlus90Days() },
  { key: 'next_quarter', label: 'Next Qtr', getValue: () => getNextQuarter() },
  { key: 'next_year', label: 'Next Year', getValue: () => getNextYear() },
]

/**
 * Create default presets that calculate dates in the specified timezone.
 * Use this when the org has a configured timezone.
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 */
export function createDefaultPresets(timezone: string): DatePreset[] {
  return [
    { key: 'tomorrow', label: 'Tomorrow', getValue: () => getTomorrow(timezone) },
    { key: 'next_week', label: 'Next Week', getValue: () => getNextWeek(timezone) },
    { key: 'next_month', label: 'Next Month', getValue: () => getNextMonth(timezone) },
    { key: 'next_quarter', label: 'Next Qtr', getValue: () => getNextQuarter(timezone) },
    { key: 'next_year', label: 'Next Year', getValue: () => getNextYear(timezone) },
  ]
}

/**
 * Create extended presets that calculate dates in the specified timezone.
 * @param timezone - IANA timezone (e.g., 'America/New_York')
 */
export function createExtendedPresets(timezone: string): DatePreset[] {
  return [
    { key: 'today', label: 'Today', getValue: () => getToday(timezone) },
    { key: 'tomorrow', label: 'Tomorrow', getValue: () => getTomorrow(timezone) },
    { key: 'next_week', label: 'Next Week', getValue: () => getNextWeek(timezone) },
    { key: 'next_month', label: 'Next Month', getValue: () => getNextMonth(timezone) },
    { key: 'plus_30', label: '+30d', getValue: () => getPlus30Days(timezone) },
    { key: 'plus_90', label: '+90d', getValue: () => getPlus90Days(timezone) },
    { key: 'next_quarter', label: 'Next Qtr', getValue: () => getNextQuarter(timezone) },
    { key: 'next_year', label: 'Next Year', getValue: () => getNextYear(timezone) },
  ]
}

/**
 * Check if a date matches a preset (for highlighting active preset button).
 */
export function matchesPreset(date: Dayjs | null, preset: DatePreset): boolean {
  if (!date) return false
  const presetDate = preset.getValue()
  return date.isSame(presetDate, 'day')
}

/**
 * Find which preset (if any) matches the given date.
 */
export function findMatchingPreset(
  date: Dayjs | null,
  presets: DatePreset[]
): DatePreset | undefined {
  if (!date) return undefined
  return presets.find((preset) => matchesPreset(date, preset))
}
